import { useState, useEffect } from 'react';

/**
 * Hook to detect internet connection speed and determine asset quality level
 * Uses Network Information API when available, falls back to effectiveType
 * 
 * In development mode, can be overridden via URL parameter: ?assetQuality=low_quality|medium_quality|original
 * 
 * @returns {Object} { quality: 'low_quality' | 'medium_quality' | 'original', effectiveType: string }
 */
export const useConnectionSpeed = () => {
  const [quality, setQuality] = useState('medium_quality'); // Default to medium
  const [effectiveType, setEffectiveType] = useState('unknown');

  useEffect(() => {
    const isDevelopment = process.env.NODE_ENV === 'development';
    
    // Check for URL parameter override (dev mode only)
    if (isDevelopment) {
      const urlParams = new URLSearchParams(window.location.search);
      // Support both assetQuality (new) and modelQuality (legacy) for backward compatibility
      const qualityParam = urlParams.get('assetQuality') || urlParams.get('modelQuality');
      
      if (qualityParam) {
        // Validate the quality parameter (original only allowed in dev via URL param)
        const validQualities = ['low_quality', 'medium_quality', 'original'];
        if (validQualities.includes(qualityParam)) {
          setQuality(qualityParam);
          setEffectiveType('dev-override');
          console.log(`🎨 [DEV] Asset quality overridden via URL parameter: ${qualityParam}`);
          return; // Skip connection detection when override is active
        } else {
          console.warn(`⚠️ [DEV] Invalid assetQuality parameter: ${qualityParam}. Valid values: ${validQualities.join(', ')}`);
        }
      }
    }

    // Check if Network Information API is available
    const connection = navigator.connection || 
                      navigator.mozConnection || 
                      navigator.webkitConnection;

    if (!connection) {
      // Fallback: assume medium quality if API not available
      setQuality('medium_quality');
      setEffectiveType('unknown');
      return;
    }

    // Function to determine quality based on connection
    const determineQuality = () => {
      // Get connection information
      const downlink = connection.downlink; // Mbps
      const effectiveType = connection.effectiveType; // 'slow-2g', '2g', '3g', '4g'
      const saveData = connection.saveData; // User preference for data saving

      setEffectiveType(effectiveType || 'unknown');

      // If user has data saver enabled, use low quality
      if (saveData) {
        setQuality('low_quality');
        return;
      }

      // Determine quality based on effectiveType and downlink speed
      // NOTE: In production, we never use 'original' - max quality is 'medium_quality'
      // Default to medium_quality for all connections unless on a slower connection
      
      // Slow connections use low_quality:
      // - slow-2g or 2g network types
      // - Any connection with downlink < 1.5 Mbps
      if (effectiveType === 'slow-2g' || effectiveType === '2g') {
        setQuality('low_quality');
      } else if (downlink && downlink < 1.5) {
        // Slow downlink speed (< 1.5 Mbps) uses low_quality
        setQuality('low_quality');
      } else {
        // All other connections (3g with good speed, 4g, fast downlink, unknown) use medium_quality
        setQuality('medium_quality');
      }
    };

    // Determine initial quality
    determineQuality();

    // Listen for connection changes
    const handleConnectionChange = () => {
      // Don't update if we're in dev mode with an override
      if (isDevelopment) {
        const urlParams = new URLSearchParams(window.location.search);
        // Support both assetQuality (new) and modelQuality (legacy) for backward compatibility
        const qualityParam = urlParams.get('assetQuality') || urlParams.get('modelQuality');
        if (qualityParam && ['low_quality', 'medium_quality', 'original'].includes(qualityParam)) {
          return; // Keep the override, don't update based on connection changes
        }
      }
      determineQuality();
    };

    connection.addEventListener('change', handleConnectionChange);

    return () => {
      connection.removeEventListener('change', handleConnectionChange);
    };
  }, []);

  return { quality, effectiveType };
};

